// src/components/landingpage/pp-kuestion/KuestionPage.jsx
//
// The Kuestion product page — what the Applications menu opens on the
// website. Built from the "kuestion-app-page" screen of the Kuestion Web
// mockup: solo candidate and Campus cohort on one engine, real screenshots
// from the build, identity, the two modes, workflow, onboarding, analytics,
// reporting, boundaries, platforms and offerings, then the poster close.
//
// Scroll 3D comes from useScroll3D (mounted once in MasterPage) reading the
// data-rise / data-yaw / data-tilt attributes; sections carry `perspective`.
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import KuestionMark from '../../kuestion/KuestionMark';
import { PosterCTA } from '../HomeSections';
import { kq, KUESTION_PUBLIC_ADDRESS } from '../../../config/routes';
import shotDashboard from '../../../assets/images/kuestion/dashboard.png';
import shotRoster from '../../../assets/images/kuestion/roster.png';
import shotAssign from '../../../assets/images/kuestion/assign.png';
import shotCohort from '../../../assets/images/kuestion/cohort.png';
import shotStudent from '../../../assets/images/kuestion/student.png';
import shotRunner from '../../../assets/images/kuestion/runner.png';
import shotWizard from '../../../assets/images/kuestion/wizard.png';
import './KuestionPage.css';

const SHOTS = [
  { src: shotRoster, alt: 'Student roster showing full login IDs', title: 'Roster', body: 'Every row shows the full login ID, not just the code.', yaw: -1 },
  { src: shotAssign, alt: 'Assignment builder with mode and window', title: 'Assignment builder', body: 'Template, mode, window and who sits it — one screen.', yaw: 0 },
  { src: shotCohort, alt: 'Cohort analytics by module', title: 'Cohort analytics', body: 'Strength per module across the whole class.', yaw: 1 },
  { src: shotStudent, alt: 'Student dashboard', title: 'Student dashboard', body: 'One exam waiting, and honest practice numbers.', yaw: -1 },
  { src: shotRunner, alt: 'Exam runner', title: 'Exam runner', body: 'Timer, question map, flags, and resume after a dropout.', yaw: 0 },
  { src: shotWizard, alt: 'Organisation setup showing the generated login prefix', title: 'Organisation setup', body: 'A one-time wizard that explains what is permanent.', yaw: 1 },
];

const SOLO_STEPS = [
  ['01', 'Bring your own questions', 'Upload a bank or import a data set. Your material, tagged to the domains you care about.'],
  ['02', 'Build the paper you need', 'Forty questions in sixty minutes, or a twenty-question drill on one domain. Set the pass mark to the real one.'],
  ['03', 'Sit it under the clock', 'The same timed runner a school uses, with the question map, flags and resume after a dropped connection.'],
  ['04', 'Mark it against yourself', 'Score, every wrong answer, the correct one and why — then the domain breakdown, attempt after attempt.'],
];

const DOMAINS = [
  ['Security Architecture', 44, true],
  ['Cryptography', 51, true],
  ['Identity & Access', 73, false],
  ['Security Governance', 88, false],
];

const WORKFLOW = [
  ['01', 'Name your organisation', 'A one-time wizard. You pick the name, we generate the permanent login prefix.', -1.4],
  ['02', 'Add your students', 'One at a time, or a whole class by CSV with passwords generated for you.', -0.7],
  ['03', 'Build the paper', 'Template first — sections, question types, marks, duration, pass mark — then draw from your banks.', 0],
  ['04', 'Assign it', 'Choose the mode, set an optional window, pick who sits it. Watch submissions arrive.', 0.7],
  ['05', 'Read the report', 'Pass or fail, module strengths, cohort roll-up — then export the PDF.', 1.4],
];

const MODE_TABLE = [
  ['Retakes', ['Never', true], 'Unlimited'],
  ['Score shown on submit', 'No', 'Yes'],
  ['Which answers were wrong', 'No', 'Yes'],
  ['Correct answers & explanations', ['No', true], 'Yes'],
  ['Student dashboard & topic strengths', 'No', 'Yes'],
  ['Examiner sees everything', 'Yes', 'Yes'],
];

const PLATFORMS = [
  ['Sit an assigned exam', 'Yes', 'Yes'],
  ['Practice and self-scoring', 'Yes', 'Yes'],
  ['Read results and analytics', 'Yes', 'Yes'],
  ['Build templates and papers', 'Yes', null],
  ['Student management & CSV import', 'Yes', null],
  ['PDF export', 'Yes', null],
];

const OFFERINGS = [
  ['Solo study — practice, self-scoring, domain strengths', 'Yes', 'Yes', 'Yes'],
  ['Your own question banks & data imports', '1 bank', 'Unlimited', 'Unlimited'],
  ['Organisation & student logins', '—', 'Yes', 'Yes'],
  ['Exam templates', '1', 'Unlimited', 'Unlimited'],
  ['Assigned students per exam', '10', '120', 'Unlimited'],
  ['Bulk CSV import', '—', 'Yes', 'Yes'],
  ['Proctored mode', '—', 'Yes', 'Yes'],
  ['Module strength reporting', 'Summary', 'Full', 'Full'],
  ['PDF reports — student & module', '—', 'Yes', 'Branded'],
  ['Teacher seats', '1', '1', 'Per department'],
  ['Support', 'Docs', 'Email', 'Premium'],
];

const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

/**
 * A hovered gallery card scales 1.42× about its own centre, which at the ends
 * of a row would push it past the section padding and get clipped by the
 * page's overflow-x. Anchor the edge cards so they grow inward instead. The
 * column count comes from auto-fit, so it has to be measured, not authored.
 */
function useShotGrid() {
  const ref = useRef(null);
  useEffect(() => {
    const grid = ref.current;
    if (!grid) return undefined;
    const fit = () => {
      const cols = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length || 1;
      Array.from(grid.children).forEach((cell, i) => {
        const f = cell.querySelector('.rw-kq__shot-frame');
        if (!f) return;
        if (cols < 2) { f.classList.add('is-single'); f.style.transformOrigin = '50% 50%'; return; }
        f.classList.remove('is-single');
        const col = i % cols;
        f.style.transformOrigin = `${col === 0 ? '0%' : col === cols - 1 ? '100%' : '50%'} 50%`;
      });
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);
  return ref;
}

export default function KuestionPage() {
  const navigate = useNavigate();
  // Kuestion has its own door (Separation contract v2.1 §1); this page is
  // where a visitor is told so.
  const open = () => navigate(kq('login'));
  const gridRef = useShotGrid();

  return (
    <main className="rw-kq">
      {/* ── head ─────────────────────────────────────────────────────── */}
      <section className="rw-kq__head">
        <div>
          <div className="rw-kq__lockup">
            <span className="rw-kq__tile">
              <KuestionMark bodyFill="currentColor" leafFill="currentColor" glyphFill="var(--color-accent)" />
            </span>
            <span className="rw-kicker">Application &middot; Examination platform</span>
          </div>
          <h1 className="rw-kq__title">Build the exam.<br />Read the result.</h1>
          <p className="rw-kq__lede">
            Kuestion serves one person sitting a certification alone and a teacher running a cohort of
            two hundred &mdash; on the same engine. Either way what comes back is not a mark but a report:
            pass or fail against your threshold, strength module by module, ready as a PDF.
          </p>
          <div className="rw-kq__actions">
            <button type="button" className="rw-btn rw-btn--primary" onClick={open}>
              Sign in or sign up
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
            </button>
            <a href="#kq-modes" className="rw-btn rw-btn--secondary">See the two modes</a>
            <a href={kq('kb')} target="_blank" rel="noopener noreferrer" className="rw-btn rw-btn--ghost" title="Knowledge base — opens in a new tab">
              Knowledge base
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 4h6v6" /><line x1="20" y1="4" x2="11" y2="13" /><path d="M18 14.5V19a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 19V7.5A1.5 1.5 0 0 1 5 6h4.5" /></svg>
            </a>
          </div>
          <p className="rw-kq__host">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 6 15 12 9 18" /></svg>
            <span>Kuestion lives at <strong>{KUESTION_PUBLIC_ADDRESS}</strong> &mdash; its own sign-in, nothing else on it.</span>
          </p>
        </div>
        <div className="rw-kq__summary">
          <div><span>Solo</span><strong>One candidate, one certification</strong></div>
          <div><span>Campus</span><strong>A teacher and their whole cohort</strong></div>
          <div><span>Modes</span><strong>Proctored sitting or open practice</strong></div>
          <div><span>Output</span><strong>One PDF report per candidate</strong></div>
        </div>
      </section>

      {/* ── two ways in ──────────────────────────────────────────────── */}
      <section className="rw-kq__section">
        <h2 className="rw-kq__h2">Two ways in</h2>
        <p className="rw-kq__p">Same question banks, same templates, same analytics. The difference is whether anyone else is involved.</p>
        <div className="rw-kq__two">
          <div className="rw-kq__card rw-kq__card--ink" data-rise="1" data-yaw="-0.6">
            <div className="rw-kq__card-top">
              <span className="rw-tag rw-tag--accent">On your own</span>
              <span className="rw-kq__who">No organisation</span>
            </div>
            <span className="rw-kq__icon"><Icon><path d="M4 4.5h6a3 3 0 0 1 3 3V21a2.2 2.2 0 0 0-2.2-2.2H4z" /><path d="M20 4.5h-6a3 3 0 0 0-3 3V21a2.2 2.2 0 0 1 2.2-2.2H20z" /></Icon></span>
            <h3>Sitting CISSP next month</h3>
            <p>Load your own bank, build the paper you actually need, and sit it under the clock as often as you like. Nobody assigns you anything and nobody sees your scores.</p>
            <div className="rw-kq__rows rw-kq__rows--push">
              <div>Full feedback every time &mdash; answers and explanations</div>
              <div>Module strengths that show what to revise tonight</div>
              <div>Sign up and start &mdash; no institution required</div>
            </div>
          </div>
          <div className="rw-kq__card rw-kq__card--surface" data-rise="1" data-yaw="0.6">
            <div className="rw-kq__card-top">
              <span className="rw-tag rw-tag--outline">Campus</span>
              <span className="rw-kq__who">One teacher, many students</span>
            </div>
            <span className="rw-kq__icon"><Icon><path d="M22 10 12 5 2 10l10 5 10-5Z" /><path d="M6 12v5c0 1.66 2.69 3 6 3s6-1.34 6-3v-5" /></Icon></span>
            <h3>Running a cohort of two hundred</h3>
            <p>Create an organisation, issue logins, assign papers with a window and a mode, and read the class back module by module.</p>
            <div className="rw-kq__rows rw-kq__rows--push">
              <div>Student logins with no email addresses needed</div>
              <div>Proctored sittings that reveal nothing</div>
              <div>Per-student and per-module PDF reports</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── dashboard band ───────────────────────────────────────────── */}
      <section className="rw-kq__band">
        <div className="rw-kq__band-inner rw-kq__band-inner--tight">
          <div data-rise="0.7" data-tilt="0.6" className="rw-kq__hero-shot">
            <div className="rw-kq__frame"><img src={shotDashboard} alt="The Kuestion examiner dashboard" /></div>
          </div>
          <div className="rw-kq__caption">
            <strong>The examiner dashboard</strong>
            <span>Everything needing your attention, before you go looking for it.</span>
          </div>
        </div>
      </section>

      {/* ── inside the app ───────────────────────────────────────────── */}
      <section className="rw-kq__section rw-kq__section--shots">
        <div className="rw-kq__shots-head">
          <span className="rw-kicker rw-kicker--dim">Inside the app</span>
          <span>Six screens from the working build &mdash; hover any of them to enlarge.</span>
        </div>
        <div className="rw-kq__shots-grid" ref={gridRef}>
          {SHOTS.map((s) => (
            <div className="rw-kq__shot" key={s.title} data-rise="1" data-yaw={s.yaw}>
              <div className="rw-kq__shot-frame"><img src={s.src} alt={s.alt} /></div>
              <div className="rw-kq__shot-title">{s.title}</div>
              <div className="rw-kq__shot-body">{s.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── studying alone ───────────────────────────────────────────── */}
      <section className="rw-kq__band">
        <div className="rw-kq__band-inner">
          <div className="rw-kicker">Studying alone</div>
          <h2 className="rw-kq__h2 rw-kq__h2--narrow">The exam is in five weeks and nobody is setting you homework.</h2>
          <p className="rw-kq__p rw-kq__p--wide">
            Certification study is a solo problem: a thousand practice questions, no idea which of the eight domains
            is going to fail you, and no teacher to tell you. Kuestion runs exactly the same engine for one person
            as it does for a school &mdash; you are just the examiner and the candidate at once.
          </p>
          <div className="rw-kq__split">
            <div className="rw-kq__split-main" data-rise="0.6">
              <div className="rw-kq__steps">
                {SOLO_STEPS.map(([n, t, b]) => (
                  <div key={n}><span className="rw-kq__no">{n}</span><span><strong>{t}</strong><span>{b}</span></span></div>
                ))}
              </div>
            </div>
            <div className="rw-kq__split-side" data-rise="1" data-yaw="0.6">
              <div className="rw-kq__panel rw-kq__panel--lift">
                <div className="rw-kq__panel-head"><span>Your own progress</span><span className="rw-kq__panel-meta">CISSP &middot; 7 attempts</span></div>
                <div className="rw-kq__panel-stats">
                  <div><span>Latest mock</span><strong className="is-accent">742 / 1000</strong></div>
                  <div><span>Your pass mark</span><strong>700</strong></div>
                  <div><span>Since attempt 1</span><strong>+118</strong></div>
                </div>
                <div className="rw-kq__panel-body">
                  <div className="rw-kq__label">Weakest domains &mdash; revise these</div>
                  {DOMAINS.map(([name, pct, weak]) => (
                    <div className="rw-kq__bar" key={name}>
                      <div className="rw-kq__bar-head"><span>{name}</span><strong className={weak ? 'is-accent' : undefined}>{pct}%</strong></div>
                      <div className="rw-kq__bar-track"><div className={`rw-kq__bar-fill${weak ? ' is-weak' : ''}`} style={{ width: `${pct}%` }} /></div>
                    </div>
                  ))}
                </div>
                <div className="rw-kq__panel-foot">Nobody else can see this. There is no teacher, no cohort and no ranking on a solo account.</div>
              </div>
            </div>
          </div>
          <div className="rw-kq__chips">
            <span className="rw-tag rw-tag--neutral">Unlimited retakes</span>
            <span className="rw-tag rw-tag--neutral">Answers &amp; explanations</span>
            <span className="rw-tag rw-tag--neutral">Domain strengths</span>
            <span className="rw-tag rw-tag--neutral">Score history</span>
            <span className="rw-tag rw-tag--neutral">Your own PDF report</span>
            <span className="rw-tag rw-tag--outline">No organisation needed</span>
          </div>
        </div>
      </section>

      {/* ── identity ─────────────────────────────────────────────────── */}
      <section className="rw-kq__section">
        <div className="rw-kicker">Identity</div>
        <h2 className="rw-kq__h2 rw-kq__h2--narrow">Students log in with one string. No email, no invitation, no reset link.</h2>
        <p className="rw-kq__aside">Campus only &mdash; a solo candidate just signs up with an email like any other Riftworks account.</p>
        <p className="rw-kq__p rw-kq__p--wide">
          Your organisation gets a six-character prefix. You choose each student&rsquo;s six-character code. Together
          they make the only credential a candidate ever needs &mdash; which means a class of thirty can be onboarded
          without a single email address between them.
        </p>
        <div className="rw-kq__split rw-kq__split--center">
          <div className="rw-kq__panel rw-kq__split-main" data-rise="0.6">
            <div className="rw-kq__panel-head">What a student types</div>
            <div className="rw-kq__panel-body rw-kq__panel-body--surface">
              <div className="rw-kq__id"><span className="p">RASTER</span><span className="d">-</span><span>A7K2M9</span></div>
              <div className="rw-kq__id-legend"><span>Organisation &middot; permanent</span><span>Student &middot; you choose</span></div>
            </div>
          </div>
          <div className="rw-kq__facts rw-kq__split-side" data-rise="1" data-yaw="0.6">
            <div><strong>Case does not matter</strong><span>Typed in lower case on a phone keyboard, it still works.</span></div>
            <div><strong>Rename the school freely</strong><span>Only the internal prefix is fixed. The display name is yours to change, any time.</span></div>
            <div><strong>One form for everyone</strong><span>Teachers and students use the same login box. The shape of the identifier tells us which you are &mdash; nobody lands on the wrong tab.</span></div>
            <div><strong>Students never self-register</strong><span>Accounts exist because you created them. That is the whole access model.</span></div>
          </div>
        </div>
      </section>

      {/* ── two modes ────────────────────────────────────────────────── */}
      <section className="rw-kq__section" id="kq-modes">
        <h2 className="rw-kq__h2">Two modes, one paper</h2>
        <p className="rw-kq__p rw-kq__p--wide">
          Mode belongs to the assignment, not the template &mdash; so the same paper can be a formal sitting for one
          class and open practice for another. Pick it when you assign.
        </p>
        <div className="rw-kq__two rw-kq__two--gap">
          <div className="rw-kq__card rw-kq__card--ink rw-kq__card--surface" data-rise="1" data-yaw="-0.6">
            <div className="rw-kq__card-top"><span className="rw-tag rw-tag--accent">Proctored</span><span className="rw-kq__who">When the mark counts</span></div>
            <h3>One sitting. Nothing leaks.</h3>
            <div className="rw-kq__rows">
              <div><strong>No retakes</strong> &mdash; one assignment, one attempt, enforced server-side.</div>
              <div><strong>No answer key on submit</strong> &mdash; absent from the response entirely, not hidden in it.</div>
              <div><strong>You release the result</strong> &mdash; the student sees a receipt, you see everything.</div>
            </div>
          </div>
          <div className="rw-kq__card" data-rise="1" data-yaw="0.6">
            <div className="rw-kq__card-top"><span className="rw-tag rw-tag--outline">Self-assessment</span><span className="rw-kq__who">When practice counts</span></div>
            <h3>As many goes as they need.</h3>
            <div className="rw-kq__rows">
              <div><strong>Unlimited retakes</strong> &mdash; the clock is optional, the repetition is the point.</div>
              <div><strong>Full feedback on submit</strong> &mdash; score, wrong answers, correct ones, explanations.</div>
              <div><strong>Builds their dashboard</strong> &mdash; module strengths they can act on alone.</div>
            </div>
          </div>
        </div>
        <div className="rw-kq__table-wrap">
          <table className="rw-table" style={{ minWidth: 560 }}>
            <thead><tr><th /><th>Proctored</th><th>Self-assessment</th></tr></thead>
            <tbody>
              {MODE_TABLE.map(([label, p, s]) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td>{Array.isArray(p) ? <strong className="is-accent">{p[0]}</strong> : p}</td>
                  <td>{s}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── examiner workflow ────────────────────────────────────────── */}
      <section className="rw-kq__section">
        <div className="rw-kicker">Examiner workflow</div>
        <h2 className="rw-kq__h2 rw-kq__h2--narrow rw-kq__h2--gap">Five steps from an empty account to a graded class</h2>
        <div className="rw-kq__flow">
          {WORKFLOW.map(([n, t, b, yaw]) => (
            <div key={n} data-rise="1" data-yaw={yaw}>
              <div className="rw-kq__no rw-kq__no--block">{n}</div>
              <h4>{t}</h4>
              <p>{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── onboarding a class ───────────────────────────────────────── */}
      <section className="rw-kq__band">
        <div className="rw-kq__band-inner">
          <div className="rw-kq__split rw-kq__split--center">
            <div className="rw-kq__split-main" data-rise="0.5">
              <div className="rw-kicker">Onboarding a class</div>
              <h2 className="rw-kq__h2 rw-kq__h2--narrow">Thirty students in one upload.</h2>
              <p className="rw-kq__p">
                Four columns, one file. Leave the password column blank and we generate compliant ones. The file is
                validated as a whole before anything is written &mdash; if two rows are wrong, nothing is imported and
                your roster is exactly as it was.
              </p>
              <div className="rw-kq__rows rw-kq__rows--soft">
                <div><strong>Per-row errors, no partial imports</strong> &mdash; you fix the file, not the database.</div>
                <div><strong>Passwords shown once</strong> &mdash; downloadable as a CSV or a printable handout on the spot.</div>
                <div><strong>Deletion says what it destroys</strong> &mdash; hard delete, typed confirmation, no recovery path pretended.</div>
              </div>
            </div>
            <div className="rw-kq__split-side" data-rise="1" data-yaw="0.5">
              <div className="rw-kq__panel">
                <div className="rw-kq__panel-head">class-9b.csv</div>
                <div className="rw-kq__table-wrap">
                  <table className="rw-table" style={{ minWidth: 420 }}>
                    <thead><tr><th>student_code</th><th>first_name</th><th>last_name</th><th>password</th></tr></thead>
                    <tbody>
                      <tr><td>A7K2M9</td><td>Ayesha</td><td>Khan</td><td className="is-dim">blank</td></tr>
                      <tr><td>B3P8L1</td><td>Marcus</td><td>Oyelaran</td><td className="is-dim">blank</td></tr>
                      <tr><td>C9Q4T7</td><td>Sofia</td><td>Lindqvist</td><td className="is-dim">blank</td></tr>
                    </tbody>
                  </table>
                </div>
                <div className="rw-kq__panel-foot">Blank means generate. It is the expected path, not a shortcut.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── analytics ────────────────────────────────────────────────── */}
      <section className="rw-kq__section">
        <div className="rw-kq__split rw-kq__split--center">
          <div className="rw-kq__split-main" data-rise="0.5">
            <div className="rw-kicker">Analytics</div>
            <h2 className="rw-kq__h2 rw-kq__h2--narrow">A mark tells you who failed. A module tells you why.</h2>
            <p className="rw-kq__p">
              You define the modules your templates draw on, and every sitting is broken down against them. Strength
              is simply the share answered correctly &mdash; per student, and rolled up across the cohort.
            </p>
            <div className="rw-kq__note">
              <strong>When we will not give you a number</strong>
              <span>Below five attempted questions in a module, Kuestion reports <strong>insufficient data</strong> rather than a percentage. Two out of three is not 67% worth of anything, and a chart that claims otherwise sends you after the wrong student.</span>
            </div>
            <div className="rw-kq__chips rw-kq__chips--tight">
              <span className="rw-tag rw-tag--neutral">Per-student strength</span>
              <span className="rw-tag rw-tag--neutral">Cohort roll-up</span>
              <span className="rw-tag rw-tag--neutral">Score history</span>
              <span className="rw-tag rw-tag--neutral">Weakest-first ranking</span>
            </div>
          </div>
          <div className="rw-kq__split-side rw-kq__split-side--wide" data-rise="1" data-yaw="0.6">
            <div className="rw-kq__frame rw-kq__frame--lift"><img src={shotCohort} alt="Module strength across the cohort, including an insufficient-data row" /></div>
          </div>
        </div>
      </section>

      {/* ── reporting ────────────────────────────────────────────────── */}
      <section className="rw-kq__section">
        <div className="rw-kicker">Reporting</div>
        <h2 className="rw-kq__h2 rw-kq__h2--narrow rw-kq__h2--gap">Two report shapes, built in the background</h2>
        <div className="rw-kq__flow rw-kq__flow--3">
          <div data-rise="1" data-yaw="-0.8">
            <span className="rw-kq__icon rw-kq__icon--sm"><Icon><path d="M15 20v-1.6a3.4 3.4 0 0 0-3.4-3.4H6.4A3.4 3.4 0 0 0 3 18.4V20" /><circle cx="9" cy="8" r="3.4" /><line x1="18" y1="8" x2="18" y2="14" /><line x1="21" y1="11" x2="15" y2="11" /></Icon></span>
            <h4>By student</h4>
            <p>One candidate&rsquo;s full record &mdash; every sitting, both modes, module strengths and the trend across attempts. The document you hand to a parent.</p>
          </div>
          <div data-rise="1" data-yaw="0">
            <span className="rw-kq__icon rw-kq__icon--sm"><Icon><line x1="3" y1="21" x2="21" y2="21" /><rect x="5" y="12" width="4" height="6" /><rect x="11" y="7" width="4" height="11" /><rect x="17" y="14" width="4" height="4" /></Icon></span>
            <h4>By module</h4>
            <p>One module across the whole cohort &mdash; who is carrying it, who is behind, and whether the topic needs teaching again.</p>
          </div>
          <div data-rise="1" data-yaw="0.8">
            <span className="rw-kq__icon rw-kq__icon--sm"><Icon><circle cx="12" cy="12" r="9.5" /><polyline points="12 7 12 12 15.5 14" /></Icon></span>
            <h4>Asked for, not waited on</h4>
            <p>Rendering is the heaviest job in the platform, so it runs in the background. Request it, carry on working, download it when it lands.</p>
          </div>
        </div>
      </section>

      {/* ── boundaries ───────────────────────────────────────────────── */}
      <section className="rw-kq__band">
        <div className="rw-kq__band-inner">
          <div className="rw-kicker">Boundaries</div>
          <h2 className="rw-kq__h2 rw-kq__h2--narrow">Who can see what, decided in the schema</h2>
          <p className="rw-kq__p rw-kq__p--wide">Organisation data is stored separately from platform data, so isolation is structural rather than a filter someone can forget to write.</p>
          <div className="rw-kq__three">
            <div className="rw-kq__card rw-kq__card--ink rw-kq__card--tight" data-rise="1" data-yaw="-0.8">
              <strong>You see your organisation</strong>
              <span>Every student you created, every result they produced. Nothing from anyone else&rsquo;s.</span>
            </div>
            <div className="rw-kq__card rw-kq__card--ink rw-kq__card--tight" data-rise="1" data-yaw="0">
              <strong>A student sees themselves</strong>
              <span>Never a classmate, never a ranking, never cohort data. Results flow one way &mdash; to you.</span>
            </div>
            <div className="rw-kq__card rw-kq__card--accent rw-kq__card--tight" data-rise="1" data-yaw="0.8">
              <strong className="is-accent">Nobody sees across organisations</strong>
              <span>Including us. There is no all-organisations screen, by design and not by oversight.</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── platforms & offerings ────────────────────────────────────── */}
      <section className="rw-kq__section rw-kq__section--flat">
        <h2 className="rw-kq__h2 rw-kq__h2--table">Where it runs</h2>
        <div className="rw-kq__table-wrap rw-kq__table-wrap--gap">
          <table className="rw-table" style={{ minWidth: 640 }}>
            <thead><tr><th /><th>Web</th><th>Android &amp; iOS</th></tr></thead>
            <tbody>
              {PLATFORMS.map(([label, web, mobile]) => (
                <tr key={label}><td>{label}</td><td>{web}</td><td>{mobile || <span className="is-dim">Web only</span>}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <h2 className="rw-kq__h2 rw-kq__h2--table">Current offerings</h2>
        <div className="rw-kq__table-wrap">
          <table className="rw-table" style={{ minWidth: 640 }}>
            <thead><tr><th /><th>Free</th><th>Professional</th><th>Institution</th></tr></thead>
            <tbody>
              {OFFERINGS.map((row) => (
                <tr key={row[0]}><td>{row[0]}</td><td>{row[1]}</td><td>{row[2]}</td><td>{row[3]}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="rw-kq__foot">Runs in the browser &mdash; nothing to install for students. Tiers shown for proposal purposes.</p>
      </section>

      <PosterCTA
        title="Start with one paper, alone or with a class."
        body="Load a bank, sit a mock, see which domain is going to fail you. The cohort can come later."
        action="Sign in or sign up"
        onAction={open}
      />
    </main>
  );
}
